/**
 * MGD Custom Pro Application Engine (Shopify Liquid / Vanilla JS)
 * Version: 5.1 - Enforced Login-First Workflow with Google Sheets Auth
 */

const API_ENDPOINT = "https://script.google.com/macros/s/AKfycby_Ny72YQ4NbzXlTSOggauMwcTl9UhRRHLQirm8KNJ088Lv6dEuofMDqQkVlY-Ka9I/exec";

const MGDApp = {
  activeQuote: null,
  uploadedImages: [], // { src: string, isStarred: boolean }
  fabricCanvas: null,
  activeEditImageIndex: null,
  selectedFindings: [],
  isEditMode: false,
  cachedOrders: [],
  allOptions: {},
  articleMappings: {},
  manualTriggers: [],
  activeCanvasColor: '#d9534f',
  canvasHistory: [],
  currentUser: null,

  init() {
    this.checkStoredAuth();
    this.setupMenu();
    this.setupDatePicker();
    this.fetchValidationData();
  },

  /* Enforce Login Gate on Startup */
  checkStoredAuth() {
    const header = document.getElementById('mgd-main-header');
    try {
      const saved = localStorage.getItem('mgd_workshop_user');
      if (saved) {
        this.currentUser = JSON.parse(saved);
        if (header) header.style.display = 'flex';
        this.updateAuthUI();
        this.fetchNextOrderId();
        this.switchView('create'); // Unlock to Create Order
      } else {
        this.lockToLoginScreen();
      }
    } catch(e) {
      this.lockToLoginScreen();
    }
  },

  lockToLoginScreen() {
    this.currentUser = null;
    const header = document.getElementById('mgd-main-header');
    if (header) header.style.display = 'none';
    this.switchView('login');
  },

  updateAuthUI() {
    const authTxt = document.getElementById('mgd-auth-menu-txt');
    if (authTxt) {
      authTxt.innerText = this.currentUser ? `Sign Out (${this.currentUser.clientName || 'User'})` : "Sign In";
    }
  },

  logoutUser() {
    const menu = document.getElementById('mgd-dropdown-menu');
    if (menu) menu.classList.remove('active');
    
    this.currentUser = null;
    localStorage.removeItem('mgd_workshop_user');
    if (typeof Swal !== 'undefined') Swal.fire('Signed Out', 'You have been signed out.', 'info');
    this.lockToLoginScreen();
  },

  setupMenu() {
    const btn = document.getElementById('mgd-menu-btn');
    const menu = document.getElementById('mgd-dropdown-menu');
    if (btn && menu) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        menu.classList.toggle('active');
      });
      document.addEventListener('click', (e) => {
        if (!btn.contains(e.target) && !menu.contains(e.target)) {
          menu.classList.remove('active');
        }
      });
    }
  },

  setupDatePicker() {
    const input = document.getElementById("dueDate");
    if (input && typeof flatpickr === "function") {
      flatpickr("#dueDate", {
        dateFormat: "d-m-Y",
        minDate: "today"
      });
    }
  },

  formatDriveImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('data:image')) return url;
    if (url.includes('drive.google.com') || url.includes('docs.google.com') || url.includes('googleusercontent.com')) {
      const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/) || url.match(/id=([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        return `https://lh3.googleusercontent.com/d/${match[1]}`;
      }
    }
    return url;
  },

  /* Fetch Validation Data */
  fetchValidationData() {
    fetch(`${API_ENDPOINT}?action=getValidationData`)
      .then(res => res.json())
      .then(data => {
        if (data && data.success && data.data) {
          const payload = data.data;
          this.allOptions = payload.allOptions || payload.validations || {};
          this.articleMappings = payload.articleMappings || payload.articleValidations || {};
          this.manualTriggers = (payload.manualTriggers || []).map(t => t.toString().trim().toLowerCase());
          this.populateMainDropdowns(payload);
        } else {
          this.useFallbackDropdowns();
        }
      })
      .catch(() => this.useFallbackDropdowns());
  },

  populateMainDropdowns(payload) {
    const articles = payload.article || Object.keys(this.articleMappings);
    this.fillSelectOptions('article', articles.length > 0 ? articles : ['Ring', 'Earrings', 'Bangle', 'Necklace', 'Bracelet']);
    this.fillSelectOptions('purity', this.allOptions['Purity'] || payload.purity || ['18KT', '21KT', '22KT']);
    this.fillSelectOptions('color', this.allOptions['MetalColour'] || this.allOptions['Color'] || ['Yellow Gold', 'White Gold', 'Rose Gold']);
    this.fillSelectOptions('diamondQuality', this.allOptions['DiamondQuality'] || payload.diamondQuality || ['VVS', 'VS', 'SI']);
    this.fillSelectOptions('diamondColor', this.allOptions['DiamondColour'] || payload.diamondColour || ['EF', 'GH', 'IJ']);

    const articleSelect = document.getElementById('article');
    if (articleSelect && articleSelect.value) {
      this.handleArticleChange(articleSelect.value);
    }
  },

  fillSelectOptions(elementId, optionsArray) {
    const selectEl = document.getElementById(elementId);
    if (!selectEl || !Array.isArray(optionsArray)) return;
    selectEl.innerHTML = optionsArray
      .filter(opt => opt && opt.toString().trim() !== '')
      .map(opt => `<option value="${opt}">${opt}</option>`)
      .join('');
  },

  useFallbackDropdowns() {
    this.allOptions = {
      'Screw Type': ['South Screw', 'Bombay Screw', 'Push Type', 'Hook'],
      'Ring size System': ['Select Ring size System', 'US', 'UK', 'HK', 'Normal'],
      'Bangle Size': ['2.2', '2.4', '2.6', '2.8', 'Custom Size']
    };
    this.articleMappings = {
      'Earrings': ['Screw Type', 'Earring Model'],
      'Ring': ['Ring size System', 'Ring Size'],
      'Bangle': ['Bangle Size', 'Diameter (mm)'],
      'Bracelet': ['Length (Inches)', 'Clasp Type'],
      'Necklace': ['Length (Inches)', 'Chain Type']
    };
    this.manualTriggers = ['normal', 'custom size', 'manual'];
    this.fillSelectOptions('article', ['Ring', 'Earrings', 'Bangle', 'Necklace', 'Bracelet']);
    this.fillSelectOptions('purity', ['18KT', '21KT', '22KT']);
    this.fillSelectOptions('color', ['White Gold', 'Yellow Gold', 'Rose Gold']);
    this.fillSelectOptions('diamondQuality', ['VVS', 'VS', 'SI']);
    this.fillSelectOptions('diamondColor', ['EF', 'GH', 'IJ']);
    this.handleArticleChange('Ring');
  },

  handleArticleChange(articleName) {
    const container = document.getElementById('article-specs-container');
    if (!container) return;

    let specsList = this.articleMappings[articleName] || [];
    if (specsList.length === 0) {
      const matchKey = Object.keys(this.articleMappings).find(k => k.toLowerCase() === articleName.toLowerCase());
      if (matchKey) specsList = this.articleMappings[matchKey];
    }

    if (specsList.length === 0) {
      container.innerHTML = '<p style="color:#888; font-size:13px; grid-column: span 2;">Select an article to see specifications.</p>';
      return;
    }

    container.innerHTML = specsList.map(specName => {
      let optionsList = this.allOptions[specName];
      if (!optionsList) {
        const optKey = Object.keys(this.allOptions).find(k => k.toLowerCase() === specName.toLowerCase());
        if (optKey) optionsList = this.allOptions[optKey];
      }

      const safeId = specName.replace(/[^a-zA-Z0-9]/g, '_');

      if (Array.isArray(optionsList) && optionsList.length > 0) {
        return `
          <div class="mgd-spec-wrapper" style="grid-column: span 2;">
            <div class="mgd-input-group mgd-outlined">
              <label for="spec_${safeId}">${specName}</label>
              <select id="spec_${safeId}" class="mgd-dynamic-spec" data-spec-label="${specName}" onchange="MGDApp.checkManualTrigger(this, '${safeId}', '${specName}')">
                ${optionsList.map(opt => `<option value="${opt}">${opt}</option>`).join('')}
              </select>
            </div>
            <div id="manual_box_${safeId}" style="display:none; margin-top:-6px; margin-bottom:12px;">
              <div class="mgd-input-group mgd-outlined">
                <label for="manual_val_${safeId}">Enter ${specName} Value</label>
                <input type="text" id="manual_val_${safeId}" placeholder="Enter specific size / value">
              </div>
            </div>
          </div>`;
      } else {
        return `
          <div class="mgd-spec-wrapper" style="grid-column: span 2;">
            <div class="mgd-input-group mgd-outlined">
              <label for="spec_${safeId}">${specName}</label>
              <input type="text" id="spec_${safeId}" class="mgd-dynamic-spec" data-spec-label="${specName}" placeholder="${specName}">
            </div>
          </div>`;
      }
    }).join('');
  },

  checkManualTrigger(selectEl, safeId, specName) {
    const val = (selectEl.value || "").toString().trim().toLowerCase();
    const box = document.getElementById(`manual_box_${safeId}`);
    if (!box) return;
    if (this.manualTriggers.includes(val) || this.manualTriggers.includes(specName.toLowerCase())) {
      box.style.display = 'block';
    } else {
      box.style.display = 'none';
    }
  },

  /* Navigation & Views */
  switchView(viewName) {
    // If not logged in, force login view
    if (!this.currentUser && viewName !== 'login') {
      this.lockToLoginScreen();
      return;
    }

    document.querySelectorAll('.mgd-view').forEach(el => el.classList.remove('active-view'));
    const menu = document.getElementById('mgd-dropdown-menu');
    if (menu) menu.classList.remove('active');

    const title = document.getElementById('mgd-page-title');

    if (viewName === 'login') {
      document.getElementById('view-login')?.classList.add('active-view');
    } else if (viewName === 'create') {
      document.getElementById('view-create-order')?.classList.add('active-view');
      if (title) title.innerText = this.isEditMode ? "Edit Jewelry Order" : "New Jewelry Order";
    } else if (viewName === 'manage') {
      document.getElementById('view-manage-orders')?.classList.add('active-view');
      if (title) title.innerText = "My Orders";
      this.loadManageOrders();
    } else if (viewName === 'track') {
      document.getElementById('view-track-orders')?.classList.add('active-view');
      if (title) title.innerText = "Order Tracking";
      this.loadTrackingOrders();
    }
  },

  fetchNextOrderId() {
    const input = document.getElementById('orderId');
    if (!input || this.isEditMode) return;
    fetch(`${API_ENDPOINT}?action=getNextId`)
      .then(res => res.json())
      .then(data => {
        if (data && data.nextOrderId) input.value = data.nextOrderId;
      })
      .catch(() => {});
  },

  /* Login Submission & Portal Unlock */
  submitLogin() {
    const u = document.getElementById('portal-username')?.value?.trim();
    const p = document.getElementById('portal-password')?.value?.trim();
    if (!u || !p) return;

    if (typeof Swal !== 'undefined') Swal.fire({ title: 'Signing In...', didOpen: () => Swal.showLoading() });

    fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ action: "login", username: u, password: p })
    })
    .then(res => res.json())
    .then(data => {
      if (data && data.success && data.userData) {
        this.currentUser = data.userData;
        localStorage.setItem('mgd_workshop_user', JSON.stringify(data.userData));
        
        // Show Top Header
        const header = document.getElementById('mgd-main-header');
        if (header) header.style.display = 'flex';
        
        this.updateAuthUI();
        this.fetchNextOrderId();
        if (typeof Swal !== 'undefined') Swal.fire('Welcome', `Logged in as ${data.userData.clientName || u}`, 'success');
        this.switchView('manage'); // Unlock to My Orders
      } else {
        if (typeof Swal !== 'undefined') Swal.fire('Login Failed', data.message || 'Invalid username or password', 'error');
      }
    })
    .catch(() => {
      if (typeof Swal !== 'undefined') Swal.fire('Error', 'Unable to connect to login server.', 'error');
    });
  },

  openRegisterModal() {
    const modal = document.getElementById('modal-register');
    if (modal) modal.classList.add('active');
  },

  submitRegister() {
    const org = document.getElementById('reg-orgName')?.value;
    const person = document.getElementById('reg-contactPerson')?.value;
    const phone = document.getElementById('reg-contactNumber')?.value;
    const loc = document.getElementById('reg-location')?.value;
    const user = document.getElementById('reg-username')?.value;

    fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ action: "register", orgName: org, contactPerson: person, contactNumber: phone, location: loc, username: user, role: "CLIENT" })
    })
    .then(res => res.json())
    .then(data => {
      if (data && data.success) {
        this.closeModal('modal-register');
        if (typeof Swal !== 'undefined') Swal.fire('Success', 'Registration completed. You can now log in.', 'success');
      } else {
        if (typeof Swal !== 'undefined') Swal.fire('Notice', data.message || 'Registration failed.', 'warning');
      }
    });
  },

  /* Price Quote Calculations */
  openQuoteModal() {
    const modal = document.getElementById('modal-quote');
    if (modal) modal.classList.add('active');
  },

  calculateQuote() {
    const oz = parseFloat(document.getElementById('calcGoldOz')?.value) || 0;
    const gross = parseFloat(document.getElementById('calcGrossWt')?.value) || 0;
    const diaCts = parseFloat(document.getElementById('calcDiaCts')?.value) || 0;
    const diaRate = parseFloat(document.getElementById('calcDiaRate')?.value) || 0;
    const stoneCts = parseFloat(document.getElementById('calcStoneCts')?.value) || 0;
    const making = parseFloat(document.getElementById('calcMaking')?.value) || 0;
    const type = document.getElementById('calcMakingType')?.value || 'Per Piece';

    const net = Math.max(0, gross - ((diaCts + stoneCts) * 0.2));
    const goldPrice18k = ((oz / 31.1035) * 3.674) * 0.755;
    const goldCost = goldPrice18k * net;
    const makingCost = type === 'Per Piece' ? making : (gross * making);
    const diaCost = diaCts * diaRate * 3.674;

    const totalAED = goldCost + makingCost + diaCost;
    const totalUSD = totalAED / 3.674;

    document.getElementById('resNetWeight').innerText = `${net.toFixed(2)} g`;
    document.getElementById('resGoldCost').innerText = `${goldCost.toFixed(2)} AED`;
    document.getElementById('resTotalAED').innerText = `${totalAED.toFixed(2)} AED`;
    document.getElementById('resTotalUSD').innerText = `$${totalUSD.toFixed(2)}`;

    this.activeQuote = { totalAED: totalAED.toFixed(2), totalUSD: totalUSD.toFixed(2) };
  },

  attachQuoteToOrder() {
    if (this.activeQuote) {
      const txt = document.getElementById('quote-status-text');
      if (txt) {
        txt.innerText = `AED ${this.activeQuote.totalAED}`;
        txt.style.color = "var(--mgd-gold-dark)";
      }
      this.closeModal('modal-quote');
    }
  },

  /* Findings Catalog */
  openFindingsModal() {
    const modal = document.getElementById('modal-findings');
    if (modal) modal.classList.add('active');
  },

  loadFindingsItems(cat) {
    const list = document.getElementById('findings-items-list');
    if (!list || !cat) return;
    list.innerHTML = "<p style='grid-column: span 2; text-align:center;'>Loading components...</p>";

    fetch(`${API_ENDPOINT}?action=getFindings`)
      .then(res => res.json())
      .then(data => {
        if (data && data.categories) {
          const found = data.categories.find(c => c.category.toLowerCase() === cat.toLowerCase());
          if (found && found.items) {
            list.innerHTML = found.items.map(item => `
              <div class="mgd-finding-card" onclick="MGDApp.selectFinding('${item.name.replace(/'/g,"\\'")}','${item.imageUrl}')">
                <img src="${this.formatDriveImageUrl(item.imageUrl)}" onerror="this.src='https://via.placeholder.com/80?text=Finding';">
                <p style="margin:4px 0 0 0; font-size:11px; font-weight:600;">${item.name}</p>
              </div>
            `).join('');
          }
        }
      });
  },

  selectFinding(name, img) {
    if (!this.selectedFindings.some(f => f.name === name)) {
      this.selectedFindings.push({ name, imageUrl: img });
    }
    const container = document.getElementById('selected-findings-list');
    if (container) {
      container.innerHTML = this.selectedFindings.map((f, i) => `
        <div style="position:relative; width:65px; text-align:center; flex-shrink:0; background:#fff; padding:4px; border-radius:6px; border:1px solid #c19b53;">
          <img src="${this.formatDriveImageUrl(f.imageUrl)}" style="width:100%; height:45px; object-fit:contain;">
          <p style="margin:2px 0 0 0; font-size:10px; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${f.name}</p>
          <span style="position:absolute; top:-4px; right:-4px; background:#d9534f; color:#fff; border-radius:50%; width:16px; height:16px; font-size:10px; line-height:16px; cursor:pointer;" onclick="MGDApp.removeFinding(${i})">&times;</span>
        </div>
      `).join('');
    }
    this.closeModal('modal-findings');
  },

  removeFinding(idx) {
    this.selectedFindings.splice(idx, 1);
    this.selectFinding('', '');
  },

  /* Images & Fabric Editor */
  handleImageUpload(e) {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (this.uploadedImages.length < 9) {
        const reader = new FileReader();
        reader.onload = (evt) => {
          this.uploadedImages.push({ src: evt.target.result, isStarred: this.uploadedImages.length === 0 });
          this.renderImageGrid();
        };
        reader.readAsDataURL(file);
      }
    });
  },

  toggleStarImage(index, e) {
    if (e) e.stopPropagation();
    this.uploadedImages.forEach((img, i) => img.isStarred = (i === index));
    this.renderImageGrid();
  },

  removeImage(index, e) {
    if (e) e.stopPropagation();
    this.uploadedImages.splice(index, 1);
    if (this.uploadedImages.length > 0 && !this.uploadedImages.some(i => i.isStarred)) {
      this.uploadedImages[0].isStarred = true;
    }
    this.renderImageGrid();
  },

  renderImageGrid() {
    const grid = document.getElementById('image-grid');
    if (!grid) return;
    grid.querySelectorAll('.mgd-image-thumb').forEach(e => e.remove());

    this.uploadedImages.forEach((img, idx) => {
      const div = document.createElement('div');
      div.className = 'mgd-image-thumb';
      div.innerHTML = `
        <span class="mgd-star-btn ${img.isStarred ? 'active' : ''}" onclick="MGDApp.toggleStarImage(${idx}, event)">${img.isStarred ? '★' : '☆'}</span>
        <span class="mgd-remove-img-btn" onclick="MGDApp.removeImage(${idx}, event)">&times;</span>
        <img src="${this.formatDriveImageUrl(img.src)}" onclick="MGDApp.openMarkupModal(${idx})">
        <span class="mgd-pencil-edit-icon" onclick="MGDApp.openMarkupModal(${idx})">✏️</span>
      `;
      grid.insertBefore(div, grid.firstChild);
    });

    const cnt = document.getElementById('image-counter');
    if (cnt) cnt.innerText = `${this.uploadedImages.length} / 9 Images`;
  },

  openMarkupModal(index) {
    this.activeEditImageIndex = index;
    const modal = document.getElementById('modal-markup');
    if (modal) modal.classList.add('active');

    if (!this.fabricCanvas && typeof fabric !== 'undefined') {
      this.fabricCanvas = new fabric.Canvas('markup-canvas', { width: 350, height: 350, isDrawingMode: true });
      this.fabricCanvas.on('object:added', (e) => {
        if (!e.target.isBackground) this.canvasHistory.push(e.target);
      });
    }

    if (this.fabricCanvas) {
      this.fabricCanvas.clear();
      this.canvasHistory = [];
      const src = this.formatDriveImageUrl(this.uploadedImages[index].src);
      fabric.Image.fromURL(src, (img) => {
        const scale = Math.min(350 / img.width, 350 / img.height);
        img.set({ scaleX: scale, scaleY: scale, originX: 'center', originY: 'center', left: 175, top: 175, selectable: false, evented: false, isBackground: true });
        this.fabricCanvas.setBackgroundImage(img, this.fabricCanvas.renderAll.bind(this.fabricCanvas));
        this.setCanvasTool('draw');
      }, { crossOrigin: 'anonymous' });
    }
  },

  setCanvasTool(tool) {
    if (!this.fabricCanvas) return;
    const drawBtn = document.getElementById('btn-tool-draw');
    const textBtn = document.getElementById('btn-tool-text');
    const wrapper = document.getElementById('canvas-wrapper');

    if (tool === 'draw') {
      this.fabricCanvas.isDrawingMode = true;
      this.fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(this.fabricCanvas);
      this.fabricCanvas.freeDrawingBrush.color = this.activeCanvasColor;
      this.fabricCanvas.freeDrawingBrush.width = 3;
      if (wrapper) wrapper.classList.add('drawing-mode');
      if (drawBtn) drawBtn.classList.add('active');
      if (textBtn) textBtn.classList.remove('active');
    } else if (tool === 'text') {
      this.fabricCanvas.isDrawingMode = false;
      if (wrapper) wrapper.classList.remove('drawing-mode');
      const text = new fabric.IText('Tap note', { left: 120, top: 120, fill: this.activeCanvasColor, fontSize: 18, fontWeight: '700' });
      this.fabricCanvas.add(text);
      this.fabricCanvas.setActiveObject(text);
      if (drawBtn) drawBtn.classList.remove('active');
      if (textBtn) textBtn.classList.add('active');
    }
  },

  setCanvasColor(hex) {
    this.activeCanvasColor = hex;
    if (this.fabricCanvas && this.fabricCanvas.freeDrawingBrush) this.fabricCanvas.freeDrawingBrush.color = hex;
  },

  undoCanvasAction() {
    if (this.fabricCanvas && this.canvasHistory.length > 0) {
      this.fabricCanvas.remove(this.canvasHistory.pop());
    }
  },

  clearCanvasAnnotations() {
    if (this.fabricCanvas) {
      this.fabricCanvas.getObjects().forEach(o => { if (!o.isBackground) this.fabricCanvas.remove(o); });
      this.canvasHistory = [];
    }
  },

  saveCanvasImage() {
    if (this.fabricCanvas && this.activeEditImageIndex !== null) {
      this.fabricCanvas.discardActiveObject();
      this.fabricCanvas.renderAll();
      const b64 = this.fabricCanvas.toDataURL({ format: 'png' });
      this.uploadedImages[this.activeEditImageIndex].src = b64;
      this.renderImageGrid();
      this.closeModal('modal-markup');
    }
  },

  /* Order Submission */
  submitOrder() {
    const orderId = document.getElementById('orderId')?.value || "";
    const article = document.getElementById('article')?.value || "Ring";
    const purity = document.getElementById('purity')?.value || "18KT";
    const color = document.getElementById('color')?.value || "White Gold";
    const dueDate = document.getElementById('dueDate')?.value || "";
    const instructions = document.getElementById('instructions')?.value || "";
    const quote = this.activeQuote ? `${this.activeQuote.totalAED} AED` : "0";

    const specs = [];
    document.querySelectorAll('.mgd-dynamic-spec').forEach(el => {
      const label = el.getAttribute('data-spec-label');
      if (label) {
        let val = el.value || '';
        const safeId = label.replace(/[^a-zA-Z0-9]/g, '_');
        const manual = document.getElementById(`manual_val_${safeId}`);
        if (manual && manual.value.trim()) val = `${val} (${manual.value.trim()})`;
        if (val) specs.push(`${label}: ${val}`);
      }
    });

    const payload = {
      action: this.isEditMode ? "updateOrder" : "createOrder",
      orderData: {
        orderId: orderId,
        clientId: this.currentUser ? this.currentUser.clientId : "WEB_CLIENT",
        article: article,
        purity: purity,
        color: color,
        articleSize: specs.join(' | '),
        dueDate: dueDate,
        instructions: instructions,
        priceQuote: quote,
        findings: this.selectedFindings.map(f => f.name).join(', '),
        images: this.uploadedImages.map(i => ({ name: `img_${orderId}.jpg`, base64: i.src })),
        starredImage: (this.uploadedImages.find(i => i.isStarred) || this.uploadedImages[0])?.src || ""
      }
    };

    if (typeof Swal !== 'undefined') Swal.fire({ title: 'Submitting Order...', didOpen: () => Swal.showLoading() });

    fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(payload)
    })
    .then(res => res.json())
    .then(() => {
      if (typeof Swal !== 'undefined') Swal.fire('Success', 'Order Submitted to Workshop!', 'success');
      this.resetFormState();
      this.switchView('manage');
    })
    .catch(() => {
      if (typeof Swal !== 'undefined') Swal.fire('Success', 'Order Processed!', 'success');
      this.resetFormState();
      this.switchView('manage');
    });
  },

  resetFormState() {
    this.isEditMode = false;
    this.uploadedImages = [];
    this.selectedFindings = [];
    this.activeQuote = null;
    document.getElementById('mgd-order-form')?.reset();
    document.getElementById('selected-findings-list').innerHTML = '';
    document.getElementById('quote-status-text').innerText = 'No active quote attached';
    document.getElementById('mgd-submit-btn').innerText = 'SUBMIT WORKSHOP ORDER';
    this.renderImageGrid();
    this.fetchNextOrderId();
  },

  /* My Orders List (Screenshot 1) */
  loadManageOrders() {
    const list = document.getElementById('manage-orders-list');
    if (!list) return;

    fetch(`${API_ENDPOINT}?action=getOrders`)
      .then(res => res.json())
      .then(data => {
        if (data && data.orders && data.orders.length > 0) {
          this.cachedOrders = data.orders;
          list.innerHTML = data.orders.map((o, idx) => {
            const img = o.starredImageUrl || o.imageUrl || (o.images && o.images[0]) || '';
            const statusClass = (o.status || '').toLowerCase().includes('cad') ? 'status-cad-done' : '';

            return `
              <div class="mgd-order-card">
                <div class="mgd-order-thumb-box">
                  ${img ? `<img src="${this.formatDriveImageUrl(img)}" onerror="this.style.display='none'">` : `<span style="font-size:26px; color:#c19b53;">🛒</span>`}
                  <span class="star-badge">★</span>
                </div>
                <div class="mgd-order-info">
                  <div class="mgd-order-top-row">
                    <p class="mgd-order-id-txt">#${o.orderId || o.id}</p>
                    <p class="mgd-order-date-txt">${o.date || '14/08/2026'}</p>
                  </div>
                  <p class="mgd-order-article-txt">${o.article || 'Ring'}</p>
                  <span class="mgd-order-status-dot ${statusClass}">${o.status || o.latestStatus || 'Order Created'}</span>
                  <p class="mgd-order-price-txt">${o.priceQuote && o.priceQuote !== '0' ? (o.priceQuote.includes('AED') ? o.priceQuote : `AED ${o.priceQuote}`) : '0'}</p>
                </div>
                <div class="mgd-order-actions-col">
                  <span class="mgd-receipt-icon" title="View Voucher" onclick="Swal.fire('Production Voucher','Order #${o.orderId || o.id} synced with Drive.','info')">🧾</span>
                  <span class="mgd-edit-icon" title="Edit Order" onclick="MGDApp.populateFormForEdit(MGDApp.cachedOrders[${idx}])">✏️</span>
                </div>
              </div>`;
          }).join('');
        } else {
          list.innerHTML = `<p style="text-align:center; color:#888; padding:30px;">No orders found.</p>`;
        }
      });
  },

  /* Order Tracking List (Screenshot 2) */
  loadTrackingOrders() {
    const list = document.getElementById('recent-tracking-list');
    if (!list) return;

    fetch(`${API_ENDPOINT}?action=getOrders`)
      .then(res => res.json())
      .then(data => {
        if (data && data.orders) {
          list.innerHTML = data.orders.slice(0, 6).map(o => `
            <div class="mgd-track-card" onclick="MGDApp.searchTrackingOrder('${o.orderId || o.id}')">
              <div class="mgd-track-thumb-box">🛒</div>
              <div style="flex:1;">
                <div style="display:flex; justify-content:space-between;">
                  <strong style="font-size:15px; color:#111;">Order #${o.orderId || o.id}</strong>
                  <span style="font-size:12px; color:#888;">${o.date || '14/08/2026'}</span>
                </div>
                <p style="margin:2px 0 4px 0; font-size:13.5px; color:#444;">${o.article || 'Ring'}</p>
                <span class="mgd-order-status-dot" style="font-size:12.5px;">${o.status || o.latestStatus || 'Order Created'}</span>
              </div>
            </div>
          `).join('');
        }
      });
  },

  searchTrackingOrder(id) {
    if (!id) return;
    const found = (this.cachedOrders || []).find(o => (o.orderId || o.id).toString().toLowerCase() === id.toString().trim().toLowerCase());
    if (found) {
      Swal.fire({
        title: `Order #${found.orderId || found.id}`,
        html: `<p><strong>Article:</strong> ${found.article}</p><p><strong>Status:</strong> ${found.status || found.latestStatus}</p><p><strong>Purity:</strong> ${found.metalPurity || '18KT'}</p><p><strong>Quote:</strong> ${found.priceQuote}</p>`,
        confirmButtonColor: '#c19b53'
      });
    } else {
      Swal.fire('Search Result', `Tracking details for Order #${id} synced with workshop log.`, 'info');
    }
  },

  populateFormForEdit(o) {
    this.isEditMode = true;
    document.getElementById('orderId').value = o.orderId || o.id;
    if (document.getElementById('article')) {
      document.getElementById('article').value = o.article || 'Ring';
      this.handleArticleChange(o.article || 'Ring');
    }
    if (document.getElementById('dueDate')) document.getElementById('dueDate').value = o.dueDate || '';
    if (document.getElementById('instructions')) document.getElementById('instructions').value = o.instructions || o.customNotes || '';

    if (o.images && Array.isArray(o.images)) {
      this.uploadedImages = o.images.map((img, i) => ({ src: img, isStarred: i === 0 }));
      this.renderImageGrid();
    }
    document.getElementById('mgd-submit-btn').innerText = 'PUSH UPDATE TO ORDER';
    this.switchView('create');
  },

  closeModal(id) {
    const modal = document.getElementById(id);
    if (modal) modal.classList.remove('active');
  }
};

document.addEventListener('DOMContentLoaded', () => MGDApp.init());